import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

interface ProtectedRouteProps {
  requiredRole?: string | string[];
  skipOnboarding?: boolean;
}

/**
 * Composant de route protégée
 * Redirige vers /login si l'utilisateur n'est pas authentifié
 * Redirige vers /onboarding si l'utilisateur n'a pas complété l'onboarding
 * Optionnellement vérifie les rôles requis
 */
export function ProtectedRoute({ requiredRole, skipOnboarding = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, profile } = useAuthStore();
  const location = useLocation();

  // Attendre la fin du chargement
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  // Rediriger si non authentifié
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Rediriger vers l'onboarding si pas complété (sauf si on est déjà sur /onboarding)
  if (
    !skipOnboarding &&
    profile &&
    profile.onboarding_completed === false &&
    location.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Vérifier les rôles si requis
  if (requiredRole && profile) {
    const hasRequiredRole = Array.isArray(requiredRole)
      ? requiredRole.includes(profile.role)
      : profile.role === requiredRole;

    if (!hasRequiredRole) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 shadow-lg text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            </p>
          </div>
        </div>
      );
    }
  }

  return <Outlet />;
}
